export type SupportedAssetBundleOs =
  "android"
  | "ios"
  | "linux"
  | "windows"
  | "mac"

export const FirebaseStoragePaths = {
  User: {
    getIconPathUploaded: (firebaseUid: string): string =>
      `tmp/users/${firebaseUid}/icon-uploaded`,
    getIconPathValid: (userId: string): string =>
      `users/${userId}/icon`,
    getVrmAccountImagePath: (userId: string, registrationId: string): string =>
      `users/${userId}/vrm-account-image/${registrationId}`,
  },

  SpaceMessage: {
    getPathUploaded: (firebaseUid: string, spaceKey: string, kind: "image" | "video"): string =>
      `tmp/users/${firebaseUid}/spaces/${spaceKey}/space-messages/${kind}-uploaded`,
    getPathValid: (spaceKey: string, messageId: string): string =>
      `spaces/${spaceKey}/space-messages/${messageId}`,
  },

  LiveComment: {
    getPathUploaded: (firebaseUid: string, spaceKey: string, liveId: string, kind: "image"): string =>
      `tmp/users/${firebaseUid}/spaces/${spaceKey}/lives/${liveId}/comments/${kind}-uploaded`,
    getPathValid: (spaceKey: string, liveId: string, commentId: string): string =>
      `spaces/${spaceKey}/lives/${liveId}/comments/${commentId}`,
  },

  LiveFreeBoard: {
    getPathUploaded: (firebaseUid: string, spaceKey: string, liveId: string, kind: "image"): string =>
      `tmp/users/${firebaseUid}/spaces/${spaceKey}/lives/${liveId}/live-free-boards/${kind}-uploaded`,
    getPathValid: (spaceKey: string, liveId: string, freeBoardId: string): string =>
      `spaces/${spaceKey}/lives/${liveId}/live-free-boards/${freeBoardId}`,
  },

  SelectionParticipant: {
    getImagePathUploaded: (userId: string, spaceKey: string, liveId: string, selectionId: string): string =>
      `tmp/users/${userId}/spaces/${spaceKey}/lives/${liveId}/selections/${selectionId}/participants/${userId}`,
    getImagePathValid: (spaceKey: string, liveId: string, selectionId: string, id: string): string =>
      `spaces/${spaceKey}/lives/${liveId}/selections/${selectionId}/participants/${id}`,
  },

  Voice: {
    getAudioPathUploaded: (firebaseUid: string): string =>
      `tmp/users/${firebaseUid}/voices/audio-uploaded`,
    getAudioPathValid: (userId: string, voiceId: string): string =>
      `users/${userId}/voices/${voiceId}/audio`,
  },

  VoiceReply: {
    getAudioPathUploaded: (firebaseUid: string): string =>
      `tmp/users/${firebaseUid}/voice-replies/audio-uploaded`,
    getAudioPathValid: (userId: string, voiceId: string, replyId: string): string =>
      `users/${userId}/voices/${voiceId}/voice-replies/${replyId}/audio`,
  },

  SpaceBGM: {
    getPathUploaded: (firebaseUid: string): string =>
      `tmp/space-bgms/uploaded/${firebaseUid}/bgm-uploaded`,
    getPathValid: (spaceBGMId: string): string =>
      `space-bgms/${spaceBGMId}/bgm`,
  },

  SpaceBackground: {
    getThumbnailPathUploaded: (firebaseUid: string): string =>
      `tmp/space-backgrounds/uploaded/${firebaseUid}/background-thumbnail-uploaded`,
    getThumbnailPathValid: (spaceBackgroundId: string): string =>
      `space-backgrounds/${spaceBackgroundId}/thumbnail`,
    getAssetBundlePathUploaded: (firebaseUid: string, os: "android" | "ios" | "linux", assetName: string): string =>
      `tmp/space-backgrounds/uploaded/${firebaseUid}/asset-bundle/${os}/${assetName}`,
    getAssetBundlePathValid: (spaceBackgroundId: string, os: "android" | "ios" | "linux", assetName: string): string =>
      `space-backgrounds/${spaceBackgroundId}/asset-bundle/${os}/${assetName}`,
    getAssetBundleFolderPath: (spaceBackgroundId: string): string =>
      `space-backgrounds/${spaceBackgroundId}/asset-bundle`,
    getHomeImagePathUploaded: (firebaseUid: string, index: number): string =>
      `tmp/space-backgrounds/uploaded/${firebaseUid}/home-image/${index}`,
    getHomeImagePathValid: (spaceBackgroundId: string, index: number): string =>
      `space-backgrounds/${spaceBackgroundId}/home-image/${index}`,
  },

  SpaceThumbnail: {
    getFolderPath: (spaceKey: string): string =>
      `spaces/${spaceKey}/thumbnails`,
    getPathValid: (spaceKey: string, thumbnailId: string): string =>
      `spaces/${spaceKey}/thumbnails/looks/${thumbnailId}`,
  },

  DefaultLook: {
    getThumbnailPathUploaded: (firebaseUid: string): string =>
      `tmp/default-looks/uploaded/${firebaseUid}/thumbnail-uploaded`,
    getThumbnailPathValid: (defaultLookId: string): string =>
      `default-looks/${defaultLookId}-thumbnail`,

    getAssetBundlePathUploaded: (firebaseUid: string, os: SupportedAssetBundleOs, assetName: string): string =>
      `tmp/default-looks/uploaded/${firebaseUid}/asset-bundle/${os}/${assetName}`,
    getAssetBundlePathValid: (defaultLookId: string, os: SupportedAssetBundleOs, assetName: string): string =>
      `default-looks/${defaultLookId}/asset-bundle/${os}/${assetName}`,
    getAssetBundleFolderPath: (defaultLookId: string): string =>
      `default-looks/${defaultLookId}/asset-bundle`,
  },

  InitialLook: {
    getSelectionImagePathUploaded: (firebaseUid: string, itemId: string): string =>
      `tmp/initial-looks/uploaded/${firebaseUid}/${itemId}/selection-image-uploaded`,
    getSelectionImagePathValid: (itemId: string): string =>
      `initial-looks/${itemId}/selection-image`,
  },

  Power: {
    getIconPathUploaded: (firebaseUid: string): string =>
      `tmp/powers/${firebaseUid}/icon-uploaded`,
    getIconPathValid: (powerId: string): string =>
      `powers/${powerId}/icon`,
    getEffectPathUploaded: (firebaseUid: string): string =>
      `tmp/powers/${firebaseUid}/effect-uploaded`,
    getEffectPathValid: (powerId: string): string =>
      `powers/${powerId}/effect`,
  },

  OGP: {
    getProfileOGPPath: (userId: string): string =>
      `ogp/profile/${userId}`,
    getFriendInvitationOGPPath: (inviteKey: string): string =>
      `ogp/friend-invitation/${inviteKey}`,
    getSpaceInvitationOGPPath: (inviteKey: string): string =>
      `ogp/space-invitation/${inviteKey}`,
    getLiveOGPOnPath: (spaceKey: string, liveId: string): string =>
      `ogp/live/${spaceKey}/${liveId}/on`,
    getLiveOGPOffPath: (spaceKey: string, liveId: string): string =>
      `ogp/live/${spaceKey}/${liveId}/off`,
    getItemOGPPath: (itemId: string): string =>
      `ogp/item/${itemId}`,
  },

  AdminUserIcon: {
    getIconPathUploaded: (firebaseUid: string): string =>
      `tmp/admin-user-icons/${firebaseUid}/icon-uploaded`,
  },

  ManagedImage: {
    getPath: (managedImageId: string): string =>
      `managed-images/${managedImageId}`,
    getPathUploaded: (firebaseUid: string, objectId: string): string =>
      `tmp/managed-images/${firebaseUid}/${objectId}`,
  },

  Item: {
    getMediaAccountPathUploaded: (firebaseUid: string): string =>
      `tmp/items/uploaded/${firebaseUid}/media-account-uploaded`,
    getMediaAccountPathValid: (itemId: string): string =>
      `items/${itemId}/media-account`,
    getAssetBundlePathUploaded: (firebaseUid: string, os: SupportedAssetBundleOs, assetName: string): string =>
      `tmp/items/uploaded/${firebaseUid}/asset-bundle/${os}/${assetName}`,
    getAssetBundlePathValid: (itemId: string, os: SupportedAssetBundleOs, assetName: string): string =>
      `items/${itemId}/asset-bundle/${os}/${assetName}`,
    getAssetBundleFolderPath: (itemId: string): string =>
      `items/${itemId}/asset-bundle`,
    
    getMainImagePathUploaded: (firebaseUid: string): string =>
      `tmp/items/uploaded/${firebaseUid}/main-image-uploaded`,
    getMainImagePathValid: (itemId: string): string =>
      `items/${itemId}/main-image`,
    
    getDetailImagePathUploaded: (firebaseUid: string, index: number): string =>
      `tmp/items/uploaded/${firebaseUid}/detail-image/${index}-uploaded`,
    getDetailImagePathValid: (itemId: string, index: number): string =>
      `items/${itemId}/detail-image/${index}`,
    
    getShareImagePathUploaded: (firebaseUid: string): string =>
      `tmp/items/uploaded/${firebaseUid}/share-image-uploaded`,
    getShareImagePathValid: (itemId: string): string =>
      `items/${itemId}/share-image`,
    
    getThumbnailPathUploaded: (firebaseUid: string): string =>
      `tmp/items/uploaded/${firebaseUid}/thumbnail-uploaded`,
    getThumbnailPathValid: (itemId: string): string =>
      `items/${itemId}/thumbnail`,
    getHomeImagePathUploaded: (firebaseUid: string): string =>
      `tmp/items/uploaded/${firebaseUid}/home-image-uploaded`,
    getHomeImagePathValid: (itemId: string): string =>
      `items/${itemId}/home-image`,
    
    getBGMPathUploaded: (firebaseUid: string): string =>
      `tmp/items/uploaded/${firebaseUid}/bgm-uploaded`,
    getBGMPathValid: (itemId: string): string =>
      `items/${itemId}/bgm`,
  },

  CoinExport: {
    /**
     * コインレポート CSV エクスポート用のフォルダパス（task-result id ごと）。
     * BigQuery EXPORT DATA がこの配下に `part-*.csv` を書き出す。
     */
    getFolderPath: (taskResultId: string): string =>
      `coin-exports/${taskResultId}`,
    /**
     * BigQuery EXPORT DATA に渡す uri パターン（バケットからの相対パス）。
     */
    getExportUriPattern: (taskResultId: string): string =>
      `coin-exports/${taskResultId}/part-*.csv`,
  },

  DeviceAccessExport: {
    getFolderPath: (taskResultId: string): string =>
      `device-access-exports/${taskResultId}`,
    getExportUriPattern: (taskResultId: string): string =>
      `device-access-exports/${taskResultId}/part-*.csv`,
  },

  NgWordLogExport: {
    getFolderPath: (taskResultId: string): string =>
      `ng-word-log-exports/${taskResultId}`,
    getCsvPath: (taskResultId: string): string =>
      `ng-word-log-exports/${taskResultId}/ng-word-logs.csv`,
  },
}
